import React, {useState, useEffect, useMemo, useRef} from 'react';
import { createPortal } from 'react-dom';
import Uppy from '@uppy/core';
import moment from 'moment';
// Importa los estilos de Uppy en tu archivo principal o aquí si usas CSS modules

import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import {collection, getDocs, query, where, doc, updateDoc, getDoc} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {db, storage} from "../../firebase.ts";
import {Country, LoadAllCountriesUseCase} from "51trips-frontend-core";
import {CountryRepository} from "../../repositories/CountryRepository.ts";
import {matchSorter} from "match-sorter";
import {AutocompleteSearch} from "../AutocompleteSearch";
import { OptionWithData } from '../../types';
import {getFlagUrl} from "../../utils/flagUrl.ts";
import {useTranslation} from "react-i18next";
import {AddressData, ShippingAddressManager} from "./ShippingAddressManager.tsx";
import RemoteSources from "@uppy/remote-sources";
import Compressor from "@uppy/compressor";
import {getAgencyAssetUrl, getAssetUrl} from "../../utils/AssetsCDN.ts";
import crossIcon from "../../assets/icons/crossIcon.png";
import DashboardModal from "@uppy/react/dashboard-modal";
import UploadIcon from "../../assets//images/upload-01.svg?react";
import es_ES from '@uppy/locales/lib/es_ES';
import en_US from '@uppy/locales/lib/en_US';


const loadAllCountriesUseCase = new LoadAllCountriesUseCase(new CountryRepository())


interface InvitationCode {
    createdDate: number;
    pdfUrl: string;
    maxCoveredPages: number;
    itinerary: {
        name: string;
        startDate: number;
        tripImageUrl: string;
        endDate: number;
        countryCodes: string[];
        pdfUrl: string;
    };
    shippingAddresses?: AddressData[];
    mediaUrls?: string[];
    totalCopies?: number;
}

// --- Iconos SVG ---
const CheckIcon = () => (
    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
    </svg>
);



const CloseIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

// --- Componente Reutilizable de Uppy ---
interface FileUploaderProps {
    path: string;
    onUploadComplete: (success: boolean, uploadURLs: string[]) => void;
    maxNumberOfFiles?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const firebaseUploader = (uppy: any, opts: { path: string }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uppy.addUploader((fileIDs: any[]) => {
        const promises = fileIDs.map((id) => {
            const file = uppy.getFile(id);
            const storageRef = ref(storage, `${opts.path}/${file.name}`);
            const uploadTask = uploadBytesResumable(storageRef, file.data);

            uppy.setFileState(id, {
                progress: {
                    uploadStarted: Date.now(),
                    uploadComplete: false,
                    percentage: 0,
                    bytesUploaded: 0,
                    bytesTotal: file.size,
                },
            });

            return new Promise((resolve, reject) => {
                uploadTask.on(
                    'state_changed',
                    (snapshot) => {
                        uppy.emit('upload-progress', uppy.getFile(id), {
                            uploader: 'Firebase',
                            bytesUploaded: snapshot.bytesTransferred,
                            bytesTotal: snapshot.totalBytes,
                        });
                    },
                    (error) => {
                        uppy.emit('upload-error', uppy.getFile(id), error);
                        reject(error);
                    },
                    () => {
                        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                            uppy.emit('upload-success', uppy.getFile(id), {
                                uploadURL: downloadURL,
                                body: { url: downloadURL }
                            });
                            resolve(downloadURL);
                        });
                    }
                );
            });
        });
        return Promise.all(promises);
    });
};

const FileUploader = ({ path, onUploadComplete, maxNumberOfFiles = 1 }: FileUploaderProps) => {
    const { t, i18n } = useTranslation();
    const uppyLocale = i18n.language.startsWith('es') ? es_ES : en_US;
    const [showDriveModal, setShowDriveModal] = useState(false);
    const [uploadedCount, setUploadedCount] = useState(0);

    const uppy = useMemo(() => {
        const u = new Uppy({
            autoProceed: false,
            restrictions: { maxNumberOfFiles: maxNumberOfFiles },
        }).use(RemoteSources,{
            companionUrl: 'https://uppy-companion-323857694960.europe-west1.run.app',
            sources: [
                'Dropbox',
                'GoogleDrive',
                'Url'
            ]
        }).use(Compressor, {
            quality: 0.9,
            retainExif: true,
            maxWidth:2160,
            maxHeight: 2160
        });
        firebaseUploader(u, { path });
        return u;
    }, [path]);

    useEffect(() => {
        uppy.on('complete', (result) => {
            console.log('Upload result:', result);
            if (result.successful && result.successful.length > 0) {
                onUploadComplete(true, result.successful.map(
                    (file: any) => file.uploadURL
                ));
                setUploadedCount(result.successful.length);
                setShowDriveModal(false);
            }
        });
    }, [uppy, onUploadComplete]);

    return (
        <>
            <div className="bg-white rounded-2xl">
                <div className="px-6 py-1 md:py-4 flex flex-row items-center text-center justify-center gap-4 cursor-pointer" onClick={() => {
                    setShowDriveModal(true);
                }}>
                    <div className="flex flex-col items-center justify-center">
                        {uploadedCount > 0 ? (
                            <p className="text-sm font-semibold text-green-600">
                                {t('tripForm.filesUploaded', { count: uploadedCount, ns: 'dashboard' })}
                            </p>
                        ) : (
                            <>
                                <p className="text-sm font-semibold mb-1">{t('tripForm.dragHere', { ns: 'dashboard' })}</p>
                                <p className="text-xs text-stone-400">{t('tripForm.or', { ns: 'dashboard' })} {' '}
                                    <span className="underline font-semibold underline-offset-2 text-neutralBlack-500">
                                        {t('tripForm.selectFiles', { ns: 'dashboard' })}
                                    </span>
                                </p>
                                <p className="text-[10px] text-stone-300 md:mt-3 tracking-wide">{t('tripForm.maxSize', { ns: 'dashboard' })}</p>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {createPortal(
                <DashboardModal
                    uppy={uppy}
                    open={showDriveModal}
                    onRequestClose={() => setShowDriveModal(false)}
                    proudlyDisplayPoweredByUppy={false}
                    theme="light"
                    locale={uppyLocale}
                />,
                document.body
            )}
        </>
    );
};


// --- Componente Contenedor de Sección (Card) ---
const SectionCard = ({ title, isCompleted, children, className = "" }) => {
    return (
        <div className={` bg-white/90 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl gap-8 p-6 relative ${className} max-h-[70dvh] overflow-y-auto overflow-x-hidden`}>
            <div className="flex justify-between items-start mb-6">
                <h3 className="text-lg font-bold text-gray-900">{title}</h3>

                {/* Lógica del Círculo Checkbox */}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center border transition-all duration-300 
          ${isCompleted ? 'bg-black ' : 'bg-white border-neutralBlack-300'}`}>
                    {isCompleted && <CheckIcon />}
                </div>
            </div>
            <div className="flex-grow">
                {children}
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL ---
export default function TripGroupForm() {
    // -- Estados para Trip Details --
    const [tripDetails, setTripDetails] = useState({
        startDate: '',
        endDate: '',
        destinations: [''], // Will store destination names
        itineraryUrl: undefined,
        mediaUrls: undefined

    });

    const [destinationSearchQuery, setDestinationSearchQuery] = useState('');

    const [, setInvitationCode] = useState<InvitationCode | null>()
    const [invitationCodeId, setInvitationCodeId] = useState<string | null>(null);
    const [backgroundImage, setBackgroundImage] = useState<string | null>()

    const {t, i18n} = useTranslation();

    const countries: Country[] = useMemo(() => {
        return loadAllCountriesUseCase.loadAll();
    }, [i18n]);

    const countryOptions: OptionWithData<Country>[] = useMemo(() => {
        if (!countries) return [];
        return countries.map((c: any) => ({
            label: c.title || c.name,
            value: c.id,
            data: c
        }));
    }, [countries]);

    const filteredCountryOptions = useMemo(() => {
        if (!destinationSearchQuery) return [];
        return matchSorter(countryOptions, destinationSearchQuery, { keys: ['label'] });
    }, [countryOptions, destinationSearchQuery]);



    // -- Estados para Address --
    const [addresses, setAddresses] = useState<AddressData[]>([]);
    const [totalCopies, setTotalCopies] = useState<number>(0);

    // --- Validaciones (Determinan si el círculo se marca) ---

    // 1. Trip Details Completo?
    const isTripDetailsComplete = useMemo(() => {
        const datesFilled = tripDetails.startDate && tripDetails.endDate;
        const destinationsFilled = tripDetails.destinations.some(d => d.trim() !== '');
        return !!(datesFilled && destinationsFilled);
    }, [tripDetails]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if(params.get("code")) {
            //search invitationCode by code
            getDocs(query(collection(db, "invitationCodes"), where("code", "==", params.get("code")))).then((querySnapshot) => {
                //get first document
                if (querySnapshot.docs.length > 0) {
                    const data = querySnapshot.docs[0];
                    setInvitationCodeId(data.id);
                    const invitationData = data.data() as InvitationCode;
                    console.log(invitationData);
                    setInvitationCode(invitationData);

                    const resolvedDestinations = (invitationData.itinerary?.countryCodes || [])
                        .filter(code => countries.some(c => c.id === code));

                    //Get invitation agency data in agencies collection with invitationData.agencyID ID
                    getDoc(doc(db, 'agencies', invitationData.agencyID || invitationData.agencyId)).then((agencyDoc) => {
                        if (agencyDoc.exists()) {
                            const agencyData = agencyDoc.data();


                            setTripDetails(prev => ({
                                ...prev,
                                startDate: moment(invitationData.itinerary?.startDate).format('YYYY-MM-DD'),
                                endDate: moment(invitationData.itinerary?.endDate).format('YYYY-MM-DD'),
                                destinations: resolvedDestinations.length > 0 ? resolvedDestinations : [''],
                                mediaUrls: invitationData.mediaUrls,
                                itineraryUrl: invitationData.itinerary?.pdfUrl,
                                agency: agencyData
                            }));

                            if (invitationData.shippingAddresses) {
                                setAddresses(invitationData.shippingAddresses);
                                // Set totalCopies from DB if available, otherwise calculate from addresses
                                if (invitationData.totalCopies !== undefined) {
                                    setTotalCopies(invitationData.totalCopies);
                                } else {
                                    const savedTotalCopies = invitationData.shippingAddresses.reduce((sum: number, addr: AddressData) => sum + (parseInt(addr.copies || '0') || 0), 0);
                                    setTotalCopies(savedTotalCopies);
                                }
                            } else if (invitationData.totalCopies !== undefined) {
                                // If no addresses but totalCopies exists
                                setTotalCopies(invitationData.totalCopies);
                            }
                            const agencyStyles = agencyData?.design?.invitationPage || {}
                            setBackgroundImage(
                             'https://cdn.51trips.com/images/pexels-eva-purrer-1937326549-28830598.webp'
                            )
                        }
                    });
                }
            })
        }
    }, [countries]);

    // 2. Archivos Completos? (Asumimos que Itinerary es obligatorio)
    // const isFilesComplete = uploads.itinerary;

    // 3. Address Completo?
    const isAddressComplete = useMemo(() => {
        const usedCopies = addresses.reduce((sum: number, addr: AddressData) => sum + (parseInt(addr.copies || '0') || 0), 0);
        return totalCopies > 0 && usedCopies === totalCopies;
    }, [addresses, totalCopies]);

    const isAllComplete = isTripDetailsComplete && !!tripDetails.itineraryUrl && (tripDetails.mediaUrls?.length > 0) && isAddressComplete;

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const prevIsAllComplete = useRef(false);

    // Se activa en un render separado al de la carga de datos de Firebase,
    // garantizando que el efecto de isAllComplete ya habrá capturado el valor inicial.
    useEffect(() => {
        if (backgroundImage && !isDataLoaded) {
            setIsDataLoaded(true);
        }
    }, [backgroundImage]);

    useEffect(() => {
        if (!isDataLoaded) {
            prevIsAllComplete.current = isAllComplete;
            return;
        }
        if (isAllComplete && !prevIsAllComplete.current) {
            setShowSuccessModal(true);
        }
        prevIsAllComplete.current = isAllComplete;
    }, [isAllComplete, isDataLoaded]);

    const updateDatabase = async (newDetails: any) => {
        if (invitationCodeId) {
            const docRef = doc(db, "invitationCodes", invitationCodeId);
            await updateDoc(docRef, {
                "itinerary.startDate": newDetails.startDate,
                "itinerary.endDate": newDetails.endDate,
                "itinerary.countryCodes": newDetails.destinations
            });
        }
    }



    // --- Handlers ---

    const handleSelectDestination = (option: OptionWithData<Country>) => {
        const destId = option.value;
        const currentDestinations = tripDetails.destinations.filter(d => d && d.trim() !== '');

        if (!currentDestinations.includes(destId)) {
            setTripDetails({ ...tripDetails, destinations: [...currentDestinations, destId] });
            updateDatabase({ ...tripDetails, destinations: [...currentDestinations, destId] });
        }
        setDestinationSearchQuery('');
    };

    const removeDestination = (destId: string) => {
        const newDestinations = tripDetails.destinations.filter(d => d !== destId);
        const newDetails = {
            ...tripDetails,
            destinations: newDestinations
        };
        setTripDetails(newDetails);
        updateDatabase(newDetails);
    };

    const handleAddressesUpdate = async (newAddresses: AddressData[]) => {
        setAddresses(newAddresses);
        if (invitationCodeId) {
            const docRef = doc(db, "invitationCodes", invitationCodeId);
            await updateDoc(docRef, {
                shippingAddresses: newAddresses
            });
        }
    };

    const handleTotalCopiesChange = async (value: number) => {
        setTotalCopies(value);
        if (invitationCodeId) {
            const docRef = doc(db, "invitationCodes", invitationCodeId);
            await updateDoc(docRef, {
                totalCopies: value
            });
        }
    };

    const managePhotosUpload = async (success: boolean, uploadURLs: string[]) => {
        if(uploadURLs.length > 0){
            const docRef = doc(db, "invitationCodes", invitationCodeId!);
            await updateDoc(docRef, {
                mediaUrls: uploadURLs
            });
            setTripDetails(prev => ({
                ...prev,
                mediaUrls: uploadURLs
            }))
        }
    }

    const manageItineraryUpload = async (success: boolean, uploadURL: string) => {
        console.log("uploadURL", uploadURL);
        if(uploadURL){
            const docRef = doc(db, "invitationCodes", invitationCodeId);
            await updateDoc(docRef, {
                "itinerary.pdfUrl": uploadURL
            });
            setTripDetails(prev => ({
                ...prev,
                itineraryUrl: uploadURL
            }));

        }
    }

    if(!tripDetails || !backgroundImage){
        return null
    }

    console.log("tripDetails", tripDetails.agency);
    return (
        <div className="min-h-screen bg-[#faf0e6] p-8 font-sans text-gray-800 overflow-y-auto px-2 "
             style={{
                 background: `url(${backgroundImage}?alt=media) center center / cover`
             }}>
            <div
                id={'overlay'}
                style={{
                    background: 'rgba(0, 0, 0, 0.25)',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 0
                }}
            />

            {/* Header */}
            <header className="text-center lg:mb-12 relative z-10 h-[10dvh]">
                <div className="lg:ml-10 flex justify-center items-center gap-4 mb-6 text-xl tracking-widest uppercase absolute z-10 left-1/2 -translate-x-1/2 lg:left-0 lg:translate-x-0">
                    <img className="invert brightness-0"
                        style={{
                            height: `20px`,
                            objectFit: 'contain'
                        }}
                        src={getAssetUrl('Logo_51T_RGB.png')}
                    />
                    <img className="size-[13px] invert brightness-0" src={crossIcon}/>
                    { tripDetails.agency?.logoName ?
                    <img className="invert brightness-0"
                        style={{ maxWidth: '100px', height: `60px`, objectFit: 'contain' }}
                        src={getAgencyAssetUrl(tripDetails.agency?.logoName)}
                        loading="lazy"
                    /> : <span>{tripDetails.agency?.name}</span>}
                </div>
            </header>

            <div className="lg:h-[100dvh] flex flex-col  justify-center items-center gap-8  z-10 lg:absolute inset-0 overflow-y-auto px-3">
                <div className="flex flex-col justify-center items-center gap-1 mb-6 text-xl    z-10">
                <h1 className="lg:text-4xl font-bold mb-2 text-white">{t('tripForm.pageTitle', { ns: 'dashboard' })}</h1>
                <p className="text-gray-600 text-xs lg:text-lg text-center text-white">{t('tripForm.pageSubtitle', { ns: 'dashboard' })}</p>
                </div>
            {/* Grid Layout */}
            <div className="  max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="flex flex-col gap-6 h-full">

                {/* COLUMNA 1: Trip Details */}
                <SectionCard title={t('tripForm.sectionTripDetails', { ns: 'dashboard' })} isCompleted={isTripDetailsComplete}>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-500 mb-1">{t('tripForm.startDate', { ns: 'dashboard' })}</label>
                            <input
                                type="date"
                                className="text-xs lg:text-sm w-full bg-neutralBlack-50 rounded p-3 focus:ring-1 focus:ring-black outline-none"
                                value={tripDetails.startDate}
                                onChange={(e) => {
                                    const newDetails = {...tripDetails, startDate: e.target.value};
                                    setTripDetails(newDetails);
                                    updateDatabase(newDetails);
                                }}
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-500 mb-1">{t('tripForm.endDate', { ns: 'dashboard' })}</label>
                            <input
                                type="date"
                                className="text-xs lg:text-sm w-full bg-neutralBlack-50 rounded p-3 focus:ring-1 focus:ring-black outline-none"
                                value={tripDetails.endDate}
                                onChange={(e) => {
                                    const newDetails = {...tripDetails, endDate: e.target.value};
                                    setTripDetails(newDetails);
                                    updateDatabase(newDetails);
                                }}
                            />
                        </div>
                        </div>
                        <div>
                            <label className="block text-sm text-gray-500 mb-1">{t('tripForm.destinations', { ns: 'dashboard' })}</label>

                             {/* Selected Destinations Tags */}
                            <div className="flex flex-wrap gap-2 mb-2">
                                {tripDetails.destinations.filter(d => d && d.trim() !== '').map((destId, idx) => {
                                    const country = countries.find(c => c.id === destId);
                                    if (!country) return null;
                                    console.log("country", country);
                                    return (
                                    <div key={idx} className="flex items-center gap-1 bg-gray-200 px-3 py-1 rounded-full text-sm bg-neutralBlack-100">
                                        <div className="w-[24px] h-[15px]  flex items-center justify-center ">
                                            <img src={getFlagUrl(country.id)} alt={`${country.title} flag`} onError={(e) => {
                                                e.currentTarget.src = 'https://storage.googleapis.com/tripsapp-prod-cdn/backgroundCoverNoImage.jpg'
                                            }}></img>
                                        </div>
                                        <span>{country ? (country.title || (country as any).name) : destId}</span>
                                        <button onClick={() => removeDestination(destId)} className="text-gray-500 hover:text-red-500">
                                            <CloseIcon />
                                        </button>
                                    </div>
                                )})}
                            </div>

                            <AutocompleteSearch
                                options={filteredCountryOptions}
                                searchQuery={destinationSearchQuery}
                                onSearchQueryChange={setDestinationSearchQuery}
                                onSelectOption={handleSelectDestination}
                                placeholder={t('tripForm.searchDestination', { ns: 'dashboard' })}
                            />
                        </div>
                    </div>
                </SectionCard>
                {/* Itinerary */}
                <SectionCard title={t('tripForm.sectionItinerary', { ns: 'dashboard' })} isCompleted={tripDetails.itineraryUrl} className="flex-1">
                    <p className="text-xs text-gray-400 mb-2">{t('tripForm.uploadItineraryDoc', { ns: 'dashboard' })}</p>
                    {tripDetails.itineraryUrl && (
                        <a
                            href={tripDetails.itineraryUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 underline mb-3 break-all"
                        >
                            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                            </svg>
                            {t('tripForm.viewUploadedItinerary', { ns: 'dashboard' })}
                        </a>
                    )}
                    <FileUploader
                        path={'invitationCodes/' + invitationCodeId + '/itinerary'}
                        onUploadComplete={(success, uploadURLs) => manageItineraryUpload(success, uploadURLs[0])}
                    />
                </SectionCard>

                </div>
                {/* COLUMNA 2: Uploads (Split in two inside the column logic if needed, or two cards stacked) */}
                {/* Según la imagen, Itinerary y Photos están en tarjetas separadas, o agrupadas.
            La imagen muestra "Itinerary" y "Photos" en bloques separados verticalmente.
            Para replicar exactamente la imagen (3 columnas), la columna central tiene 2 bloques.
        */}
                <div className="flex flex-col gap-6 h-full">


                    {/* Photos */}
                    <SectionCard title={t('tripForm.sectionPhotos', { ns: 'dashboard' })} isCompleted={tripDetails.mediaUrls?.length > 0} className="flex-1">
                        <p className="text-xs text-gray-400 mb-2">{t('tripForm.uploadPhotos', { ns: 'dashboard' })}</p>
                        <FileUploader
                            maxNumberOfFiles={500}
                            width={'100%'}
                            path={'invitationCodes/' + invitationCodeId + '/media'}
                            onUploadComplete={(status, uploadURLs) => managePhotosUpload(status, uploadURLs)}
                        />
                    </SectionCard>
                </div>

                {/* COLUMNA 3: Address */}
                <SectionCard title={t('tripForm.sectionAddress', { ns: 'dashboard' })} isCompleted={isAddressComplete}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm text-gray-500 mb-1">{t('tripForm.totalCopies', { ns: 'dashboard' })}</label>
                            <input
                                type="number"
                                className="w-full bg-neutralBlack-50 rounded p-3 focus:ring-1 focus:ring-black outline-none"
                                value={totalCopies}
                                onChange={(e) => handleTotalCopiesChange(parseInt(e.target.value) || 0)}
                                placeholder={t('tripForm.totalCopiesPlaceholder', { ns: 'dashboard' })}
                            />
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm text-gray-500 mb-2">{t('tripForm.deliveryAddresses', { ns: 'dashboard' })}</label>
                            <ShippingAddressManager
                                maxAddresses={50}
                                maxCopies={totalCopies}
                                initialAddresses={addresses}
                                handleAddressesUpdate={handleAddressesUpdate}
                            />
                        </div>
                    </div>
                </SectionCard>

            </div>
            </div>

            {showSuccessModal && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
                    <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 flex flex-col items-center text-center gap-6">
                        <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                            <CheckIcon />
                        </div>
                        <div className="flex flex-col gap-3">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {t('tripForm.successTitle', { ns: 'dashboard' })}
                            </h2>
                            <p className="text-gray-500 text-sm leading-relaxed">
                                {t('tripForm.successMessage', { ns: 'dashboard' })}
                            </p>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
